import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import isString from 'lodash.isstring';
import moment from 'moment';
import * as React from 'react';
import isURL from 'validator/lib/isURL';

export interface Props {
  featureProps: any | null;
  onBack?: (() => void) | null;
  onClose?: () => void;
}

const useStyles = makeStyles((theme: any) => ({
  grow: {
    flexGrow: 1
  },
  tableCell: {
    border: 'none',
    padding: theme.spacing(0.75, 0.5),
    verticalAlign: 'top',
    ...theme.typography.body2
  }
}));

export const IdentifySingle: React.FC<Props> = ({ featureProps, onBack, onClose }) => {
  const classes = useStyles();

  const title = (
    <Box component="div" display="flex" alignItems="center" p={1.5}>
      {onBack && (
        <Box pr={1}>
          <IconButton onClick={onBack} aria-label="back button" size="small">
            <ArrowBackIcon />
          </IconButton>
        </Box>
      )}
      <Typography
        className={classes.grow}
        align="center"
        component="h4"
        variant="subtitle2"
        noWrap={true}
      >
        {featureProps && featureProps._title}
      </Typography>
      {onClose && (
        <Box pl={1}>
          <IconButton onClick={onClose} aria-label="close button" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  // Keys for feature properties to render in component.
  // Properties with keys starting with "_" will be skipped.
  // Properties with falsy values will be skipped. Mostly to not show empty strings.
  const keys = Object.keys(featureProps).filter(key => key[0] !== '_' && featureProps[key]);

  const isoDateTest = new RegExp(
    // tslint:disable-next-line: max-line-length
    /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/
  );

  const content = featureProps ? (
    keys.length ? (
      <Box overflow="auto" p={1}>
        <Table>
          <TableBody>
            {keys.map((key: string) => {
              if (
                isString(featureProps[key]) &&
                isURL(featureProps[key], {
                  protocols: ['http', 'https']
                })
              ) {
                return (
                  <TableRow key={key}>
                    <TableCell className={classes.tableCell} colSpan={2}>
                      <Link
                        href={featureProps[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        <Box
                          component="span"
                          display="flex"
                          flexDirection="row"
                          alignItems="flex-start"
                        >
                          <Box component="span" display="flex" pr={1}>
                            <OpenInNewIcon fontSize="small" />
                          </Box>
                          <Typography component="span">{key}</Typography>
                        </Box>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              }
              if (isString(featureProps[key]) && isoDateTest.test(featureProps[key])) {
                return (
                  <TableRow key={key}>
                    <TableCell className={classes.tableCell}>{key}:</TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {moment(featureProps[key]).format('MM-DD-YYYY')}
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={key}>
                  <TableCell className={classes.tableCell}>{key}:</TableCell>
                  <TableCell className={classes.tableCell} align="right">
                    {featureProps[key]}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    ) : (
      <Box py={2}>
        <Typography align="center" variant="body2">
          No additional properties
        </Typography>
      </Box>
    )
  ) : (
    <Box py={2}>
      <Typography align="center" variant="body2">
        Feature not found
      </Typography>
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" bgcolor="background.paper">
      {title}
      <Divider />
      {content}
    </Box>
  );
};

export default IdentifySingle;
